<template>
    <component :is="parentWrapper" class="pa-0">
        <v-container
            :class="{
                'px-3 py-0 my-3': $vuetify.breakpoint.mdAndUp,
            }"
            fluid
            grid-list-md
            :white="isContainerWhite"
        >
            <v-layout row wrap>
                <v-flex xs12 md6 :class="{'pb-0': $vuetify.breakpoint.smAndDown, addressFormClass: true}">
                    <v-card :flat="flatCards || $vuetify.breakpoint.smAndDown" :color="cardColor">
                        <template v-if="!isUserLoggedIn">
                            <v-card-title
                                primary-title
                                class="justify-center"
                                :class="{'pt-0': $vuetify.breakpoint.smAndDown}"
                            >
                                <p v-if="$vuetify.breakpoint.mdAndUp" class="title mb-0">
                                    {{ "I'm new here" | translation }}
                                </p>

                                <registration-login-switcher
                                    v-show="$vuetify.breakpoint.smAndDown"
                                    v-model="mobileShowRegistration"
                                    :default-value="true"
                                    :root-step="route"
                                />
                            </v-card-title>

                            <login-form
                                v-show="$vuetify.breakpoint.smAndDown && guestCheckout === false"
                                no-title
                                :input-solo="inputSolo"
                                :input-label-outside="inputLabelOutside"
                                :is-submit-button-round="isNextBtnRound"
                                :login-btn-depressed="nextBtnDepressed"
                                card-class="text-xs-left"
                                :card-color="cardColor"
                                third-party-login-buttons-only
                            />

                            <v-card-text v-show="guestCheckout === null && mobileShowRegistration" class="pt-0">
                                <customer-guest-switcher v-model="guestCheckout" :root-step="route"/>
                            </v-card-text>
                        </template>

                        <template v-if="isUserLoggedIn || guestCheckout !== null">
                            <v-card-title v-if="isUserLoggedIn" class="title justify-center">
                                {{'Welcome' | translation}}, {{$refs.registration && $refs.registration.firstName}}!
                            </v-card-title>

                            <v-card-text>
                                <v-form ref="form" v-model="valid" lazy-validation @submit.prevent>
                                    <v-layout row wrap>
                                        <v-flex xs12>
                                            <component
                                                :is="registrationComponent"
                                                ref="registration"
                                                :ask-password="!guestCheckout"
                                                :input-solo="inputSolo"
                                                :input-label-outside="inputLabelOutside"
                                                :disabled="loading"
                                            />
                                        </v-flex>

                                        <v-flex xs12 class="pb-0">
                                            <component
                                                :is="billingAddressComponent"
                                                @addressLoaded="onBillingAddressLoad"
                                                ref="billingAddressForm"
                                                :disabled="loading"
                                                :houseNumberRequired="displayHouseNumberField || houseNumberRequired"
                                                :displayAddressFormProp="displayBillingForm"
                                                :redirect="redirect"
                                                :input-solo="inputSolo"
                                                :input-label-outside="inputLabelOutside"
                                                @submit="onBillingFormSubmit"
                                                :form-class="formClass"
                                                :button-round="isNextBtnRound"
                                                :button-depressed="nextBtnDepressed"
                                                :button-large="buttonLarge"
                                                :deactivate-shipping-address-selection="deactivateShippingAddressSelection"
                                            />
                                        </v-flex>

                                        <v-flex xs12 v-if="otherAddress" class="pt-0">
                                            <component
                                                :is="shippingAddressComponent"
                                                @addressLoaded="onShippingAddressLoad"
                                                ref="shippingAddressForm"
                                                :displayList="true"
                                                :disabled="loading"
                                                :input-solo="inputSolo"
                                                :input-label-outside="inputLabelOutside"
                                                :form-class="formClass"
                                                :button-round="isNextBtnRound"
                                                :button-depressed="nextBtnDepressed"
                                                :button-large="buttonLarge"
                                                :houseNumberRequired="displayHouseNumberField || houseNumberRequired"
                                            />
                                        </v-flex>

                                        <birthday-select
                                            class="ma-1 mt-3"
                                            v-if="birthdayField"
                                            v-model="birthday"
                                            :min-age="minAge"
                                            :input-solo="inputSolo"
                                            :input-label-outside="inputLabelOutside"
                                            :disabled="loading"
                                            :required="birthdayFieldRequired"
                                            ref="birthdaySelect"
                                        />

                                        <v-flex xs12 pb-0>
                                            <v-checkbox
                                                class="d-inline-block mt-0"
                                                :disabled="loading"
                                                v-model="agb"
                                                :rules="[rules.agb]"
                                                required
                                            >
                                                <div slot="label">
                                                    <component :is="{template: '<span>' + $language.translate('I agree with the conditions') + '</span>'}"/>
                                                </div>
                                            </v-checkbox>
                                        </v-flex>

                                        <v-flex xs12 text-xs-right pt-0>
                                            <v-btn
                                                :round="isNextBtnRound"
                                                :color="nextBtnColor"
                                                :disabled="!valid || loading"
                                                @click="submit"
                                                :depressed="nextBtnDepressed"
                                                :large="buttonLarge"
                                                block
                                            >
                                                {{ nextButtonLabel | translation }}
                                            </v-btn>
                                        </v-flex>
                                    </v-layout>
                                </v-form>
                            </v-card-text>
                        </template>
                    </v-card>
                </v-flex>

                <v-flex
                    xs12
                    md6
                    v-if="isVisibleRegistrationForm"
                    :class="{'pt-0': $vuetify.breakpoint.smAndDown}"
                >
                    <v-card :flat="flatCards || $vuetify.breakpoint.smAndDown" :color="cardColor">
                        <v-card-title
                            v-if="!isUserLoggedIn && $vuetify.breakpoint.mdAndUp"
                            class="justify-center"
                            primary-title
                            :class="{'pt-0': $vuetify.breakpoint.smAndDown}"
                        >
                            <p class="title mb-0">{{ 'I am already a customer' | translation }}</p>
                        </v-card-title>

                        <v-card-text v-if="$vuetify.breakpoint.mdAndUp && guestCheckout !== null" class="pt-0">
                            <v-btn color="primary" @click="guestCheckout = null" block depressed>
                                {{ 'Login now' | translation }}
                            </v-btn>
                        </v-card-text>

                        <template v-else-if="!isUserLoggedIn">
                            <login-form
                                no-title
                                :input-solo="inputSolo"
                                :input-label-outside="inputLabelOutside"
                                :is-submit-button-round="isNextBtnRound"
                                :login-btn-depressed="nextBtnDepressed"
                                card-class="text-xs-left"
                                :card-color="cardColor"
                            />
                        </template>

                        <page-content v-else/>
                    </v-card>
                </v-flex>
            </v-layout>
        </v-container>
    </component>
</template>

<script>
import StepBase from '@breakmedia/prime-vue-stepper/src/StepBase';
import module from './store/addressStep';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import Vue from 'vue';
import BillingAddressForm from './BillingAddress';
import BirthdaySelect from '@breakmedia/prime-vue-customer/src/BirthdaySelect';
import moment from 'moment';
import RegistrationLoginSwitcher from './RegistrationLoginSwitcher';
import CustomerGuestSwitcher from './CustomerGuestSwitcher';
import { eventManager } from '@breakmedia/prime-vue-event-manager';

const moduleName = 'CheckoutAddressStep';
const loginModuleName = 'Login';

if (!(Vue.$store.state[moduleName])) {
    Vue.$store.registerModule(moduleName, module);
}

export default {
    //do not set the name - it will break the integration with vuetify stepper
    extends: StepBase,
    components: {
        BirthdaySelect,
        RegistrationLoginSwitcher,
        CustomerGuestSwitcher,
    },
    props: {
        route: {
            default: 'address',
        },
        isNextBtnRound: Boolean,
        buttonLarge: Boolean,
        nextBtnDepressed: {
            type: Boolean,
            default: true,
        },
        nextBtnColor: {
            type: String,
            default: 'primary',
        },
        inputSolo: Boolean,
        inputLabelOutside: Boolean,
        formClass: String,
        flatCards: Boolean,
        showLoginForm: {
            default: true,
            type: Boolean,
        },
        addressFormClass: '',
        cardColor: {
            type: String,
            default: 'white'
        },
        isContainerWhite: Boolean,
        houseNumberRequired: Boolean,
        birthdayField: Boolean,
        birthdayFieldRequired: Boolean,
        minAge: Number,
        deactivateShippingAddressSelection: Boolean,
        title: String,
    },
    data: () => ({
        valid: true,
        rules: {
            required: value => !!value || 'Dieses Feld wird benötigt und darf nicht leer sein.',
            agb: value => !!value || 'Bitte stimmen sie den AGB zu.',
        },
        loading: false,
        showTermsModal: false,
        displayHouseNumberField: false, //used to be re-set from the other step within stepper.
        displayBillingForm: false, //used to be re-set from the other step within stepper.
        redirect: null, //used to be re-set from the other step within stepper.
        mobileShowRegistration: true,
    }),
    mounted() {
        const agb = this.$session.get('agb');
        if (agb) {
            this.agb = agb;
        }
        eventManager.$emit('initiateCheckout', {
            basketItems: Object.values(this.$basket.getBasketItems()),
            eventId: eventManager.getUniqueId('initiateCheckout'),
        });
    },
    computed: {
        ...mapGetters(loginModuleName, [
            'isUserLoggedIn',
        ]),
        ...mapState(BillingAddressForm.name, {
            billingAddressId: state => state.address.id,
        }),
        ...mapGetters(BillingAddressForm.name, [
            'getBirthday',
        ]),
        ...mapState('Customer', [
            'customerHasDSGVO',
        ]),
        isVisibleRegistrationForm() {
            if (this.showLoginForm) {
                if (this.$vuetify.breakpoint.mdAndUp) {
                    return true;
                }
                if (this.mobileShowRegistration === false) {
                    return true;
                }
            }
        },
        agb: {
            get() {
                return this.getAgb();
            },
            set(value) {
                this.$session.set('agb', value);
                this.setAgb(value);
            },
        },
        otherAddress: {
            get() {
                return this.getOtherAddress();
            },
            set(value) {
                this.setOtherAddress(value);
            },
        },
        birthday: {
            get() {
                const birthday = this.getBirthday;
                if (birthday) {
                    const dateParts = birthday.substr(0, birthday.indexOf('T')).split('-');
                    return {
                        day: parseInt(dateParts[2]),
                        month: parseInt(dateParts[1]),
                        year: parseInt(dateParts[0]),
                    };
                }

                return {
                    day: null,
                    month: null,
                    year: null,
                };
            },
            set(value) {
                if (value.day && value.month && value.year) {
                    let day = value.day.toString();
                    if (day.length === 1) {
                        day = `0${day}`;
                    }
                    let month = value.month.toString();
                    if (month.length === 1) {
                        month = `0${month}`;
                    }
                    const year = value.year;

                    const date = moment(`${year}-${month}-${day} 00:00 +0000`, 'YYYY-MM-DD HH:mm Z').toDate();
                    this.setBirthday(date);
                }
            },
        },
        registrationComponent() {
            let generalCountry = this.$country.getCountry();
            let billingCountry;

            if (this.$store.state['BillingAddressForm']) {
                billingCountry = this.$store.getters['BillingAddressForm/getCountry'];
            }

            let country = billingCountry ? billingCountry.toLowerCase() : generalCountry.toLowerCase();

            if (Vue.options.components['checkout-registration-' + country]) {
                return Vue.options.components['checkout-registration-' + country];
            } else {
                return Vue.options.components['checkout-registration-default'];
            }
        },
        billingAddressComponent() {
            let country = this.$country.getCountry();

            if (Vue.options.components['checkout-billing-address-' + country.toLowerCase()]) {
                return Vue.options.components['checkout-billing-address-' + country.toLowerCase()];
            } else {
                return Vue.options.components['checkout-billing-address-default'];
            }
        },
        shippingAddressComponent() {
            let country = this.$country.getCountry();

            if (Vue.options.components['checkout-shipping-address-' + country.toLowerCase()]) {
                return Vue.options.components['checkout-shipping-address-' + country.toLowerCase()];
            } else {
                return Vue.options.components['checkout-shipping-address-default'];
            }
        },
        deliveryCountry() {
            return this.$order.getDeliveryCountry();
        },
        nextButtonLabel() {
            return this.isUserLoggedIn ?
                'Continue' :
                (
                    this.guestCheckout === true ?
                        'Checkout as a guest' :
                        'Create an account'
                );
        },
        breakpoint() {
            return this.$vuetify.breakpoint;
        },
        guestCheckout: {
            get() {
                return this.getGuestCheckout();
            },
            set(val) {
                this.setGuestCheckout(val);
            },
        },
    },
    methods: {
        ...mapGetters(moduleName, [
            'getAgb',
            'getOtherAddress',
            'getGuestCheckout',
        ]),
        ...mapMutations(moduleName, [
            'setAgb',
            'setOtherAddress',
            'setGuestCheckout',
        ]),
        ...mapMutations(loginModuleName, [
            'setAuthToken',
        ]),
        ...mapMutations(BillingAddressForm.name, [
            'setBirthday',
        ]),
        ...mapActions(moduleName, [
            'registerCustomer',
            'setCustomerDataErrors',
        ]),
        ...mapActions('Checkout', [
            'setBillingAddressId',
            'setShippingAddressId',
        ]),
        ...mapActions('NotificationModal', [
            'displayNotificationModal',
        ]),
        ...mapActions('Customer', [
            'saveDSGVO',
        ]),
        ...mapMutations('Customer', [
            'setCustomerHasDSGVO',
        ]),
        validate() {
            let result = this.$refs.form.validate() && (!this.$refs.birthdaySelect || this.$refs.birthdaySelect.validate());

            if (this.$refs.registration) {
                result = this.$refs.registration.validate() && result;
            }
            result = this.$refs.billingAddressForm.validate() && result;
            result = (!this.otherAddress || this.$refs.shippingAddressForm.validate()) && result;

            return result;
        },
        submit() {
            this.resetErrors();
            if (this.validate()) {
                this.loading = true;

                if (!this.isUserLoggedIn) {
                    this.registerCustomer().then(result => {
                        if (result.success) {
                            eventManager.$emit('registrationComplete');
                            this.saveAddresses().then(() => {
                                this.loading = false;
                                this.goToSummary();
                            }).catch(e => {
                                this.loading = false;
                                this.showErrorModal();
                            });
                        } else if (result.errors) {
                            this.loading = false;
                            if (typeof result.errors === 'string') {
                                result.errors = this.$language.translate(result.errors);
                            }
                            this.setCustomerDataErrors(result.errors);
                        } else {
                            this.showErrorModal();
                            this.loading = false;
                        }
                    }).catch(e => {
                        this.showErrorModal();
                        this.loading = false;
                    });
                } else {
                    this.saveAddresses().then(() => {
                        this.loading = false;
                        this.goToSummary();
                    }).catch(e => {
                        this.loading = false;
                        this.showErrorModal();
                    });
                }
            } else if (this.$refs.billingAddressForm && this.$refs.billingAddressForm.isAddressValid() !== true) {
                this.$refs.billingAddressForm.showAddressForm(true);
            } else if (this.$refs.shippingAddressForm && this.$refs.shippingAddressForm.isAddressValid() !== true) {
                this.$refs.shippingAddressForm.showAddressForm(true);
            }
        },
        saveAddresses() {
            return this.$refs.billingAddressForm.saveAddress()
                .then(result => {
                    this.setBillingAddressId(result.id);
                    if (this.otherAddress) {
                        return this.$refs.shippingAddressForm.saveAddress().then(result => {
                            this.setShippingAddressId(result.id);
                            this.$refs.shippingAddressForm.loadAddress();
                            return result;
                        });
                    }
                })
                .then(() => {
                    this.saveDSGVO();
                });
        },
        resetErrors() {
            if (this.$refs.registration) {
                this.$refs.registration.resetErrors();
            }
        },
        showErrorModal() {
            this.displayNotificationModal({
                message: 'An unexpected problem occurred. Please try again later.',
            });
        },
        onBillingAddressLoad(address) {
            this.setBillingAddressId(address && address.id ? address.id : null);
        },
        onShippingAddressLoad(address) {
            this.setShippingAddressId(address && address.id ? address.id : null);
        },
        onBillingFormSubmit() {
            if (this.isUserLoggedIn) {
                this.saveAddresses().then(() => {
                    this.$emit('goToStep', this.redirect);
                });
            }
        },
        goToSummary() {
            this.$emit('goToStep', {
                route: 'summary',
            });
        },
    },
    watch: {
        active(value) {
            if (!value) {
                this.displayHouseNumberField = false;
                this.displayBillingForm = false;
            }
        },
        deliveryCountry: {
            handler(country) {
                if (country) {
                    this.$country.setCountry(country);
                }
            },
            immediate: true
        },
        isUserLoggedIn: {
            handler(val) {
                this.mobileShowRegistration = true;
                this.$emit('update:title', this.$language.translate(
                    val ? 'Your data' : 'Log in'
                ));
                if (!val) {
                    this.setCustomerHasDSGVO(false);
                }
            },
            immediate: true,
        },
        customerHasDSGVO(customerHasDSGVO) {
            if (customerHasDSGVO && this.billingAddressId) {
                this.goToSummary();
            }
        },
        mobileShowRegistration() {
            this.guestCheckout = null;
        },
    },
};
</script>
